import { useFormik } from "formik"
import { useContext, useEffect, useMemo } from "react"
//prime react
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { RadioButton } from "primereact/radiobutton"
import { Dropdown } from "primereact/dropdown"
import { InputText } from "primereact/inputtext"
import { MultiSelect } from "primereact/multiselect"
//styles
import styles from "../../../../styles/kiosk/KioskAdditionalAssessment.module.css"
import "../../../../styles/global.css"
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"

import {
  kioskApiPostSaveAssessmentsConfigs,
  kioskApiPostDeleteAssessmentsConfigs,
} from "../../kioskApi"
import { UserContext } from "../../../../context/userContext"
import { LogError } from "../../../../helpers/logger"
import { getFormErrorMessage, isFormFieldValid } from "../helpers/helpers"
import CustomTooltip from "../Utils/CustomTooltip"

const ConfigModal = ({
  show,
  setShow,
  deleteConfig,
  setDeleteConfig,
  assessments,
  ftvOptions,
  kiosk_organization_id,
  setUpdateList,
  assessmentsConfigs,
  editConfig,
  setEditConfig,
}) => {
  const userCtx = useContext(UserContext)
  const excludeActivityTemplateType = "activities_module"

  const filteredFtvOptions = useMemo(() => {
    return ftvOptions.filter(
      (option) =>
        option.soarTemplateType &&
        option.soarTemplateType !== excludeActivityTemplateType
    )
  }, [ftvOptions])

  const saveData = async (data) => {
    try {
      const payload = { ...data, organizationId: kiosk_organization_id }
      const result = await kioskApiPostSaveAssessmentsConfigs(
        userCtx.tokenKiosk,
        payload
      )
      setShow(false)
      setUpdateList(true)
      formik.resetForm()
      return result
    } catch (error) {
      LogError("Unable to save Additional Assessment config values", error)
      userCtx.setNotificationError("Unable to submit changes")
      formik.setSubmitting(false)
    }
  }

  const clean = () => {
    setShow(false)
    setUpdateList(true)
    setDeleteConfig(false)
    setEditConfig(null)
    formik.resetForm()
  }

  const deleteData = async (data) => {
    try {
      const payload = { id: data.id }
      const result = await kioskApiPostDeleteAssessmentsConfigs(
        userCtx.tokenKiosk,
        payload
      )
      clean()
      formik.resetForm()
      setUpdateList(true)
      return result
    } catch (error) {
      clean()
      LogError("Unable to delete Assessment configuration values", error)
      userCtx.setNotificationError("Unable to delete config")
    }
  }

  const formik = useFormik({
    initialValues: {
      id: undefined,
      assessmentId: undefined,
      description: "",
      performAt: [],
      enabled: true,
      activityCheckIn: false,
    },
    validate: (data) => {
      let errors = {}
      if (!data.assessmentId) {
        errors.assessmentId = "Assessment profile is required."
      }
      return errors
    },
    onSubmit: (data) => {
      if (deleteConfig) {
        deleteData(data)
      } else {
        saveData(data)
      }
    },
  })

  useEffect(() => {
    if (editConfig) {
      formik.setFieldValue("id", editConfig.id)
      formik.setFieldValue("assessmentId", editConfig.additionalAssessmentId)
      formik.setFieldValue("description", editConfig.description)
      formik.setFieldValue(
        "performAt",
        filteredFtvOptions
          .filter((f) => editConfig.performedAt?.includes(f.value))
          .map((f) => f.value)
      )
      formik.setFieldValue("enabled", editConfig.enabled)
      formik.setFieldValue("activityCheckIn", editConfig.activityCheckIn)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editConfig, filteredFtvOptions])

  const setAssessments = () => {
    const existingConfigs = assessmentsConfigs?.map(
      (a) => a.additionalAssessmentId
    )
    const response = editConfig
      ? assessments
      : assessments.filter((a) => !existingConfigs.includes(a.id))
    return response.map((r) => {
      return { value: r.id, name: r.name }
    })
  }

  const headerText = () => {
    if (deleteConfig) {
      return "Are you sure you want to delete this item?"
    }
    return `${editConfig ? "Edit" : "Add"} Assessment Configuration`
  }

  return (
    <Dialog visible={show} className={kioskStyles.addModal} showHeader={false}>
      <div className={kioskStyles.modalHeader}>
        {headerText()}
        <Button
          icon="pi pi-times"
          className={kioskStyles.hideModalButton}
          onClick={() => {
            clean()
          }}
          disabled={formik.isSubmitting}
        />
      </div>
      <div className={kioskStyles.modalFormContainer}>
        <hr className={kioskStyles.modalHeaderLine} />
        <form onSubmit={formik.handleSubmit}>
          <div className={kioskStyles.modalField}>
            <CustomTooltip
              label={
                "The Assessments profile for this assessment configuration in Kiosk"
              }
              position={"bottom"}
              targetId={"form_config_name"}
            />
            <label className={kioskStyles.modalFieldLabel}>
              Assessment profile *
              <i
                className={`pi pi-info-circle ${kioskStyles.info}`}
                id={"form_config_name"}
              ></i>
            </label>
            <Dropdown
              id="assessmentId"
              name="assessmentId"
              value={formik.values.assessmentId}
              onChange={formik.handleChange}
              options={setAssessments()}
              optionLabel="name"
              optionValue="value"
              className={`${kioskStyles.modalFieldInput} ${
                kioskStyles.modalDropDownInput
              } ${
                isFormFieldValid(formik, "assessmentId") &&
                kioskStyles.inputError
              }`}
              disabled={!!editConfig || deleteConfig}
            />
            {getFormErrorMessage(formik, "assessmentId")}
          </div>
          <div className={kioskStyles.modalField}>
            <CustomTooltip
              label={
                "The description for this assessment configuration in Kiosk"
              }
              position={"bottom"}
              targetId={"form_config_description"}
            />
            <label className={kioskStyles.modalFieldLabel}>
              Description
              <i
                className={`pi pi-info-circle ${kioskStyles.info}`}
                id={"form_config_description"}
              ></i>
            </label>
            <InputText
              id="description"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              className={kioskStyles.modalFieldInput}
              disabled={deleteConfig}
            />
          </div>
          <div className={kioskStyles.modalField}>
            <CustomTooltip
              label={"Where the assessment form should be completed in Kiosk"}
              position={"bottom"}
              targetId={"form_config_performAt"}
            />
            <label className={kioskStyles.modalFieldLabel}>
              Perform At
              <i
                className={`pi pi-info-circle ${kioskStyles.info}`}
                id={"form_config_performAt"}
              ></i>
            </label>
            <MultiSelect
              id={"performAt"}
              name={"performAt"}
              value={formik.values.performAt}
              onChange={formik.handleChange}
              options={filteredFtvOptions}
              optionLabel="name"
              optionValue="value"
              display="chip"
              maxSelectedLabels={5}
              className={`${kioskStyles.modalFieldInput} ${"custom-chips"} ${
                isFormFieldValid(formik, "performAt") && kioskStyles.inputError
              }`}
              disabled={deleteConfig}
            />
            {getFormErrorMessage(formik, "performAt")}
          </div>
          <div className={kioskStyles.modalField}>
            <CustomTooltip
              label={
                "Select if the assessment form should be completed in after the activity check in"
              }
              position={"bottom"}
              targetId={"form_config_activity"}
            />
            <label className={kioskStyles.modalFieldLabel}>
              Activity Check In
              <i
                className={`pi pi-info-circle ${kioskStyles.info}`}
                id={"form_config_activity"}
              ></i>
            </label>
            <div className={styles.radio_btn_wrapper}>
              <div className={styles.radio_btn}>
                <RadioButton
                  inputId={`Yes_${"activity"}`}
                  value={true}
                  name={"activityCheckIn"}
                  id={"activityCheckIn"}
                  style={{ margin: "0" }}
                  onChange={formik.handleChange}
                  checked={formik.values.activityCheckIn}
                  disabled={deleteConfig}
                />
                <label
                  htmlFor={`Yes_${"activity"}`}
                  className={styles.inline_label}
                >
                  Yes
                </label>
              </div>
              <div className={styles.radio_btn}>
                <RadioButton
                  inputId={`No_${"activity"}`}
                  value={false}
                  name={"activityCheckIn"}
                  id={"activityCheckIn"}
                  style={{ margin: "0" }}
                  onChange={formik.handleChange}
                  checked={!formik.values.activityCheckIn}
                  disabled={deleteConfig}
                />
                <label
                  htmlFor={`No_${"activity"}`}
                  className={styles.inline_label}
                >
                  No
                </label>
              </div>
            </div>
          </div>
          <div className={kioskStyles.modalField}>
            <CustomTooltip
              label={"Enable or disable this assessment configuration in Kiosk"}
              position={"bottom"}
              targetId={"form_config_enabled"}
            />
            <label className={kioskStyles.modalFieldLabel}>
              Enabled
              <i
                className={`pi pi-info-circle ${kioskStyles.info}`}
                id={"form_config_enabled"}
              ></i>
            </label>
            <div className={styles.radio_btn_wrapper}>
              <div className={styles.radio_btn}>
                <RadioButton
                  inputId={`Yes_${"test"}`}
                  value={true}
                  name={"enabled"}
                  id={"enabled"}
                  style={{ margin: "0" }}
                  onChange={formik.handleChange}
                  checked={formik.values.enabled}
                  disabled={deleteConfig}
                />
                <label
                  htmlFor={`Yes_${"test"}`}
                  className={styles.inline_label}
                >
                  Yes
                </label>
              </div>
              <div className={styles.radio_btn}>
                <RadioButton
                  inputId={`No_${"test"}`}
                  value={false}
                  name={"enabled"}
                  id={"enabled"}
                  style={{ margin: "0" }}
                  onChange={formik.handleChange}
                  checked={!formik.values.enabled}
                  disabled={deleteConfig}
                />
                <label htmlFor={`No_${"test"}`} className={styles.inline_label}>
                  No
                </label>
              </div>
            </div>
          </div>
          <div className={kioskStyles.modalFooter}>
            <Button
              label="Cancel"
              type="button"
              className={`${kioskStyles.modalFooterButton} ${kioskStyles.modalCancelButton}`}
              onClick={() => {
                clean()
              }}
              disabled={formik.isSubmitting}
            />
            <Button
              icon={formik.isSubmitting && "pi pi-spin pi-spinner"}
              label={deleteConfig ? "Delete" : "Save"}
              type="submit"
              disabled={formik.isSubmitting}
              className={`${kioskStyles.modalFooterButton} ${kioskStyles.primaryButton}`}
            />
          </div>
        </form>
      </div>
    </Dialog>
  )
}
export default ConfigModal
